import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import GenerationProgress from './GenerationProgress';
import './Home.css';

function Home() {
    const [subject, setSubject] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationQueueId, setGenerationQueueId] = useState(null);
    const [generatingTopic, setGeneratingTopic] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();

    // Debounced search for topic suggestions
    useEffect(() => {
        if (subject.length < 2) {
            setSuggestions([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`${API_URL}/api/topics/search?q=${encodeURIComponent(subject)}`);
                const data = await response.json();
                if (data.success && data.results) {
                    setSuggestions(data.results);
                }
            } catch (error) {
                console.log('Search error:', error);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [subject]);

    // Start background generation and show progress
    const startBackgroundGeneration = async (topic) => {
        if (!currentUser) {
            navigate('/auth');
            return;
        }

        setGeneratingTopic(topic);
        setIsGenerating(true);

        try {
            // OPTIMIZATION: Check for pre-built content first
            const prebuiltResponse = await fetch(`${API_URL}/api/prebuilt/${encodeURIComponent(topic)}`);
            const prebuiltData = await prebuiltResponse.json();

            if (prebuiltData.success && prebuiltData.available) {
                console.log('✨ Using pre-built content for:', topic);
                // Pre-built content available - navigate immediately!
                setIsGenerating(false);
                navigate('/learning-path', {
                    state: {
                        topic,
                        preGenerated: true,
                        prebuiltContent: {
                            learningPath: prebuiltData.learningPath,
                            lessons: prebuiltData.lessons
                        }
                    }
                });
                return;
            }

            // No pre-built content, start background generation
            const response = await fetch(`${API_URL}/api/generation/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    topic: topic
                })
            });

            const data = await response.json();

            if (data.success && data.queueId) {
                setGenerationQueueId(data.queueId);
            } else {
                // Fall back to direct navigation if generation fails to start
                console.log('Background generation unavailable, navigating directly');
                navigate('/learning-path', { state: { topic } });
            }
        } catch (error) {
            console.error('Error starting generation:', error);
            // Fall back to direct navigation
            navigate('/learning-path', { state: { topic } });
        }
    };

    const handleStartLearning = () => {
        if (subject.trim()) {
            startBackgroundGeneration(subject.trim());
        }
    };

    const handleQuickTopic = (topic) => {
        startBackgroundGeneration(topic);
    };

    const handleGenerationComplete = (data) => {
        console.log('Generation complete:', data);
        // Navigate to learning path - content is pre-generated
        navigate('/learning-path', { state: { topic: generatingTopic, preGenerated: true } });
    };

    const handleGenerationError = (error) => {
        console.error('Generation error:', error);
        // Fall back to direct navigation
        navigate('/learning-path', { state: { topic: generatingTopic } });
    };

    // Show generation progress if generating
    if (isGenerating && generationQueueId) {
        return (
            <GenerationProgress
                queueId={generationQueueId}
                topic={generatingTopic}
                onComplete={handleGenerationComplete}
                onError={handleGenerationError}
            />
        );
    }

    const quickTopics = [
        { name: 'AWS Certification', icon: '☁️', color: '#ff9900' },
        { name: 'Python', icon: '🐍', color: '#3776ab' },
        { name: 'Spanish', icon: '🇪🇸', color: '#ef4444' },
        { name: 'Real Estate', icon: '🏠', color: '#10b981' },
        { name: 'Data Science', icon: '📊', color: '#8b5cf6' },
        { name: 'JavaScript', icon: '💛', color: '#f7df1e' },
        { name: 'PMP Cert', icon: '📋', color: '#667eea' },
        { name: 'Piano', icon: '🎹', color: '#1a1a2e' },
    ];

    const categories = [
        {
            title: 'K-12 Education',
            icon: '🎒',
            topics: ['Elementary Math', 'Middle School Science', 'High School English', 'AP Classes', 'Study Skills', 'Homework Help'],
            color: '#3b82f6'
        },
        {
            title: 'Homeschool',
            icon: '🏠',
            topics: ['Curriculum Planning', 'Grade-Level Math', 'Reading & Writing', 'Science Projects', 'History', 'Life Skills'],
            color: '#8b5cf6'
        },
        {
            title: 'Test Prep',
            icon: '📝',
            topics: ['SAT', 'ACT', 'GRE', 'LSAT', 'MCAT', 'AP Exams'],
            color: '#ef4444'
        },
        {
            title: 'Tech & Coding',
            icon: '💻',
            topics: ['Python', 'JavaScript', 'React', 'AWS', 'Data Science', 'Machine Learning'],
            color: '#667eea'
        },
        {
            title: 'Languages',
            icon: '🌍',
            topics: ['Spanish', 'French', 'Japanese', 'German', 'Italian', 'Mandarin'],
            color: '#10b981'
        },
        {
            title: 'Certifications',
            icon: '📜',
            topics: ['PMP', 'AWS Solutions', 'Real Estate', 'CPA', 'CISSP', 'Six Sigma'],
            color: '#f59e0b'
        },
        {
            title: 'Creative',
            icon: '🎨',
            topics: ['Piano', 'Guitar', 'Drawing', 'Photography', 'Video Editing', 'Design'],
            color: '#ec4899'
        },
        {
            title: 'Life Skills',
            icon: '🌟',
            topics: ['Personal Finance', 'Time Management', 'Public Speaking', 'Critical Thinking', 'Leadership', 'Mindfulness'],
            color: '#14b8a6'
        }
    ];

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const firstName = userData?.displayName?.split(' ')[0] || 'Learner';

    return (
        <div className="home-app">
            {/* Header Section */}
            <header className="home-header">
                <div className="greeting">
                    <h1 className="greeting-text">{getGreeting()}, {firstName}!</h1>
                    <p className="greeting-sub">What would you like to learn today?</p>
                </div>
            </header>

            {/* Search Section */}
            <section className="search-section">
                <div className={`search-container ${isInputFocused ? 'focused' : ''} ${showSuggestions && suggestions.length > 0 ? 'has-suggestions' : ''}`}>
                    <div className="search-icon">🔍</div>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search any topic..."
                        value={subject}
                        onChange={(e) => {
                            setSubject(e.target.value);
                            setShowSuggestions(true);
                        }}
                        onFocus={() => {
                            setIsInputFocused(true);
                            setShowSuggestions(true);
                        }}
                        onBlur={() => {
                            setIsInputFocused(false);
                            // Delay hiding suggestions to allow click
                            setTimeout(() => setShowSuggestions(false), 200);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleStartLearning();
                            if (e.key === 'Escape') setShowSuggestions(false);
                        }}
                    />
                    {subject && (
                        <button className="search-go" onClick={handleStartLearning}>
                            Go
                        </button>
                    )}

                    {/* Topic Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="search-suggestions">
                            {suggestions.map((suggestion, idx) => (
                                <button
                                    key={idx}
                                    className="suggestion-item"
                                    onClick={() => {
                                        setSubject(suggestion.name);
                                        setShowSuggestions(false);
                                        startBackgroundGeneration(suggestion.name);
                                    }}
                                >
                                    <span className="suggestion-name">{suggestion.name}</span>
                                    <span className="suggestion-category">{suggestion.category}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Quick Topics */}
            <section className="quick-section">
                <h2 className="section-title">Quick Start</h2>
                <div className="quick-scroll">
                    {quickTopics.map((topic, i) => (
                        <button
                            key={i}
                            className="quick-chip"
                            onClick={() => handleQuickTopic(topic.name)}
                            style={{ '--chip-color': topic.color }}
                        >
                            <span className="chip-icon">{topic.icon}</span>
                            <span className="chip-text">{topic.name}</span>
                        </button>
                    ))}
                </div>
            </section>

            {/* Categories */}
            <section className="categories-section">
                <h2 className="section-title">Browse Categories</h2>
                <div className="categories-grid">
                    {categories.map((category, i) => (
                        <div
                            key={i}
                            className="category-card"
                            style={{ '--cat-color': category.color }}
                        >
                            <div className="category-header">
                                <span className="category-icon">{category.icon}</span>
                                <h3 className="category-title">{category.title}</h3>
                            </div>
                            <div className="category-topics">
                                {category.topics.slice(0, 4).map((topic, j) => (
                                    <button
                                        key={j}
                                        className="category-topic"
                                        onClick={() => handleQuickTopic(topic)}
                                    >
                                        {topic}
                                    </button>
                                ))}
                            </div>
                            <button
                                className="category-more"
                                onClick={() => handleQuickTopic(category.title)}
                            >
                                Explore all →
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured Banner */}
            <section className="featured-section">
                <div className="featured-banner">
                    <div className="featured-content">
                        <span className="featured-badge">🔥 Popular</span>
                        <h3 className="featured-title">AWS Cloud Practitioner</h3>
                        <p className="featured-desc">Join 12,000+ learners mastering cloud fundamentals</p>
                        <button
                            className="featured-btn"
                            onClick={() => handleQuickTopic('AWS Cloud Practitioner')}
                        >
                            Start Learning
                        </button>
                    </div>
                    <div className="featured-visual">
                        <span className="featured-emoji">☁️</span>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="how-section">
                <h2 className="section-title">How It Works</h2>
                <div className="how-steps">
                    <div className="how-step">
                        <div className="step-number">1</div>
                        <div className="step-content">
                            <h4>Choose a Topic</h4>
                            <p>Search or browse any subject you want to master</p>
                        </div>
                    </div>
                    <div className="how-step">
                        <div className="step-number">2</div>
                        <div className="step-content">
                            <h4>AI Creates Your Path</h4>
                            <p>Get a personalized curriculum built just for you</p>
                        </div>
                    </div>
                    <div className="how-step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                            <h4>Learn & Level Up</h4>
                            <p>Complete lessons, earn XP, and track your progress</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="cta-section">
                <div className="cta-card">
                    <div className="cta-text">
                        <h3>Ready to begin?</h3>
                        <p>Start your learning journey now</p>
                    </div>
                    <button
                        className="cta-btn"
                        onClick={() => document.querySelector('.search-input').focus()}
                    >
                        🚀 Let's Go
                    </button>
                </div>
            </section>
        </div>
    );
}

export default Home;
